import React from "react";
import SectionHeading from "./SectionHeading";
import { LANDING } from "@/constants/testIds";

/**
 * Fund Architecture — the platform's intended vehicle stack.
 * Vehicle names are working titles, subject to documentation and
 * regulatory authorisation. Modelled on Apollo / Blackstone / Ares
 * "Our Funds" tables, adapted for a platform in formation.
 */

const VEHICLES = [
  // Funds
  {
    family: "Fund",
    name: "KR Credit Opportunities Fund",
    term: "Open-ended evergreen",
    mandate: "Industrial Credit",
    note:
      "Senior-secured and unitranche financings across the lower-mid-market industrial complex.",
  },
  {
    family: "Fund",
    name: "KR Special Situations Fund I",
    term: "Closed-ended",
    mandate: "Bridge & Special Situations",
    note:
      "Time-sensitive financings, rescue capital and capital-event bridges with covenants and security calibrated to the exit.",
  },
  {
    family: "Fund",
    name: "KR Strategic Equity Fund I",
    term: "Closed-ended",
    mandate: "Opportunistic Industrial Equity",
    note:
      "Control and influence equity in asset-rich industrial businesses with operational and value-realisation pathways.",
  },
  {
    family: "Fund",
    name: "KR Real Estate Credit Fund I",
    term: "Closed-ended initially",
    mandate: "Industrial Real Estate Credit",
    note:
      "Asset-backed senior-secured loans against industrial, logistics and operationally critical real estate.",
  },
  {
    family: "Fund",
    name: "KR Real Assets Fund I",
    term: "Closed-ended",
    mandate: "Real Assets · Industrial",
    note:
      "Equity and equity-linked exposure to tangible industrial assets with contracted utilisation characteristics.",
  },
  {
    family: "Fund",
    name: "KR Infrastructure Fund I",
    term: "Long-duration closed-ended",
    mandate: "Industrial Infrastructure",
    note:
      "Patient capital for long-duration infrastructure assets across energy transition, logistics and onshoring.",
  },
  // Capital Channel
  {
    family: "Capital Channel",
    name: "KR Co-Investment Vehicles",
    term: "Deal-by-deal SPVs",
    mandate: "Cross-Mandate",
    note:
      "Single-asset SPVs alongside the platform funds, offered selectively to limited partners and strategic counterparties.",
  },
  // Business Line
  {
    family: "Business Line",
    name: "KR Debt Solutions",
    term: "SMA · Warehouse · Forward Flow",
    mandate: "Industrial Credit · Bespoke",
    note:
      "Programmatic credit relationships — separately managed accounts, warehouse facilities and forward-flow arrangements with institutional counterparties.",
  },
  // SMA
  {
    family: "SMA",
    name: "KR Strategic Accounts",
    term: "Single-investor / SMA",
    mandate: "Bespoke · Cross-Mandate",
    note:
      "Documented, single-investor mandates tailored to the strategic and regulatory needs of sophisticated institutional capital.",
  },
];

const FAMILIES = ["Fund", "Capital Channel", "Business Line", "SMA"];

const FundArchitecture = () => {
  return (
    <section
      id="funds"
      data-testid={LANDING.fundArchitecture}
      className="relative py-32 lg:py-44 border-t border-[var(--kr-line-soft)]"
    >
      <div className="max-w-[1480px] mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-16 items-end mb-16">
          <div className="lg:col-span-8">
            <SectionHeading
              index="05"
              label="Fund Architecture · Vehicle Stack"
              title={
                <>
                  Nine vehicles.{" "}
                  <span className="italic text-[var(--kr-gold)]">
                    Four structures.
                  </span>{" "}
                  One platform.
                </>
              }
            />
          </div>
          <div className="lg:col-span-4 lg:text-right">
            <a
              href="/funds"
              data-testid={LANDING.fundArchitectureExplore}
              className="kr-link font-mono-label text-[11px]"
            >
              View Fund Architecture →
            </a>
          </div>
        </div>

        <p className="text-[var(--kr-text-muted)] text-[16px] leading-[1.85] max-w-[80ch] mb-16">
          The intended vehicle architecture is organised around four
          structures — closed and open-ended funds, deal-by-deal co-investment
          channels, bespoke business lines and single-investor separately
          managed accounts — calibrated to the cash-flow profile, duration and
          governance preferences of sophisticated institutional capital. The
          platform's funds are intended to be domiciled in the Cayman Islands,
          with Cayman governance, corporate-services and fund-platform support
          intended through{" "}
          <a
            href="https://www.cv5capital.io/en"
            target="_blank"
            rel="noreferrer"
            className="kr-link"
          >
            CV5
          </a>
          {" "}/{" "}
          <a
            href="https://www.bellrockgroup.com/"
            target="_blank"
            rel="noreferrer"
            className="kr-link"
          >
            Bell Rock Group
          </a>
          , subject to final engagement and onboarding.
        </p>

        {/* Working titles banner */}
        <div
          className="mb-12 px-6 py-4 border border-[var(--kr-gold)]/40 inline-flex items-center gap-4"
          data-testid={LANDING.fundArchitectureFormation}
        >
          <span className="font-mono-label text-[10px] text-[var(--kr-gold)]">
            Working Titles
          </span>
          <span className="w-8 h-px bg-[var(--kr-gold)]/60" />
          <span className="text-[13px] text-[var(--kr-text-muted)]">
            Vehicle names are working titles · in formation. Subject to
            documentation and regulatory authorisation.
          </span>
        </div>

        {FAMILIES.map((family) => {
          const items = VEHICLES.filter((v) => v.family === family);
          return (
            <div key={family} className="mb-16 last:mb-0">
              <div className="flex items-baseline justify-between mb-6">
                <div className="flex items-center gap-4">
                  <span className="font-mono-label text-[10px] text-[var(--kr-gold)]">
                    {family}
                  </span>
                  <span className="w-10 h-px bg-[var(--kr-gold)]/60" />
                  <span className="font-mono-label text-[10px] text-[var(--kr-text-muted)]">
                    {String(items.length).padStart(2, "0")} Vehicle
                    {items.length > 1 ? "s" : ""}
                  </span>
                </div>
              </div>

              <ul className="border-t border-[var(--kr-line)]">
                {items.map((v) => (
                  <li
                    key={v.name}
                    data-testid={`${
                      LANDING.fundVehiclePrefix
                    }${v.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="grid lg:grid-cols-12 gap-4 lg:gap-8 py-7 border-b border-[var(--kr-line)] group hover:bg-[rgba(201,169,110,0.025)] transition-colors duration-500 items-baseline"
                  >
                    <div className="lg:col-span-4 font-serif-display text-[var(--kr-text)] text-[1.35rem] leading-[1.25] group-hover:text-[var(--kr-gold)] transition-colors duration-500">
                      {v.name}
                    </div>
                    <div className="lg:col-span-2 font-mono-label text-[10px] text-[var(--kr-text-muted)]">
                      {v.term}
                    </div>
                    <div className="lg:col-span-2 font-mono-label text-[10px] text-[var(--kr-gold)]">
                      {v.mandate}
                    </div>
                    <div className="lg:col-span-4 text-[var(--kr-text-muted)] text-[14px] leading-[1.7]">
                      {v.note}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}

        <p className="text-[var(--kr-text-dim)] text-[12px] leading-[1.7] max-w-[110ch] mt-8">
          Vehicle architecture is preparatory in nature. Documented terms,
          launch sequencing and capacity will be established in line with
          regulatory authorisation, anchor partner discussions and the
          partnership's underwriting standards.
        </p>
      </div>
    </section>
  );
};

export default FundArchitecture;
